import React from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { DailyStatsChart } from '../../../../shared/components/DailyStatsChart';
import {
    ArrowLeftIcon,
    UserGroupIcon,
    ChartBarIcon,
} from '@heroicons/react/24/outline';

export const UserAnalytics: React.FC = () => {
    const navigate = useNavigate();
    const userStats = useQuery(api.admin_analytics.getUserStats);

    if (!userStats) {
        return (
            <div className="min-h-screen bg-background pt-24">
                <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                    <div>
                        <div className="flex items-center gap-4 mb-4">
                            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                                Back
                            </Button>
                        </div>
                        <h1 className="text-3xl font-semibold text-foreground">User Analytics</h1>
                    </div>
                </div>
                <div className="flex justify-center items-center h-64">
                    <Spinner className="h-12 w-12" />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin')}>
                            <ArrowLeftIcon className="h-4 w-4 mr-2" />
                            Back
                        </Button>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-xl bg-white border-2 border-green-200">
                            <UserGroupIcon className="h-8 w-8 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-semibold text-foreground mb-1">User Analytics</h1>
                            <p className="text-base text-muted-foreground">
                                Total users, activity trends, and retention
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-8">
                    {/* Summary Stats Pills */}
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                            <span className="text-sm text-muted-foreground">Total Users: </span>
                            <span className="font-semibold text-foreground">{userStats.totalUsers}</span>
                        </div>
                        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                            <span className="text-sm text-muted-foreground">Active Users: </span>
                            <span className="font-semibold text-foreground">{userStats.activeUsers}</span>
                        </div>
                        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                            <span className="text-sm text-muted-foreground">Retention Rate: </span>
                            <span className="font-semibold text-foreground">{userStats.retentionRate}%</span>
                        </div>
                        <div className="px-4 py-2 bg-card border-2 border-border/50 rounded-full">
                            <span className="text-sm text-muted-foreground">Returning Users: </span>
                            <span className="font-semibold text-foreground">{userStats.returningUsers}</span>
                        </div>
                    </div>

                    {/* Trend Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Total Users Trends */}
                        <div className="border-2 border-border/50 bg-card rounded-xl p-5">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <UserGroupIcon className="h-5 w-5 text-primary" />
                                </div>
                                <span className="text-base font-medium text-foreground">New User Signups</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {([1, 7, 14] as const).map(days => {
                                    const val = userStats.trends[days]?.total || 0;
                                    return (
                                        <div key={days} className="text-center p-3 bg-muted/30 rounded-lg">
                                            <div className="text-xs text-muted-foreground mb-1">Last {days}d</div>
                                            <div className={`text-lg font-bold ${val > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                +{val}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Active Users Trends */}
                        <div className="border-2 border-border/50 bg-card rounded-xl p-5">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 rounded-lg bg-green-500/10">
                                    <ChartBarIcon className="h-5 w-5 text-green-600" />
                                </div>
                                <span className="text-base font-medium text-foreground">Active Users (Logged In)</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                {([1, 7, 14] as const).map(days => {
                                    const val = userStats.trends[days]?.active || 0;
                                    return (
                                        <div key={days} className="text-center p-3 bg-muted/30 rounded-lg">
                                            <div className="text-xs text-muted-foreground mb-1">Last {days}d</div>
                                            <div className={`text-lg font-bold ${val > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                                                {val}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Chart: Cumulative Total Users Over Time */}
                    <DailyStatsChart
                        data={userStats.cumulativeTotalByDay}
                        title="Total Users Over Time"
                        lineColor="#3b82f6"
                        yAxisLabel="Users"
                        tooltipSuffix="total users"
                    />

                    {/* Chart: Daily Active Users */}
                    <DailyStatsChart
                        data={userStats.dailyActiveData}
                        title="Daily Active Users"
                        lineColor="#10b981"
                        yAxisLabel="Active Users"
                        tooltipSuffix="active users"
                    />

                    {/* Retention: Login Streak Distribution */}
                    <div className="p-5 rounded-xl border-2 border-border/50 bg-card">
                        <h3 className="text-lg font-semibold text-foreground mb-4">User Retention — Login Streak Distribution</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            Shows how many consecutive days users have logged in. Higher streaks indicate better retention.
                        </p>
                        <div className="space-y-3">
                            {Object.entries(userStats.streakDistribution).map(([bucket, count]) => {
                                const maxCount = Math.max(...Object.values(userStats.streakDistribution));
                                const widthPct = maxCount > 0 ? ((count as number) / maxCount) * 100 : 0;
                                return (
                                    <div key={bucket} className="flex items-center gap-3">
                                        <span className="text-sm text-muted-foreground w-16 text-right flex-shrink-0">{bucket} days</span>
                                        <div className="flex-1 bg-muted/30 rounded-full h-7 overflow-hidden">
                                            <div
                                                className="h-full bg-primary/60 rounded-full transition-all duration-500"
                                                style={{ width: `${Math.max(widthPct, 2)}%` }}
                                            />
                                        </div>
                                        <span className="text-sm font-medium text-foreground w-12 text-right flex-shrink-0">
                                            {count as number}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
